import { Box, Button, Chip, Divider, Grid, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useGetMerchantQuery } from "../../../redux/commonRTK";
import { getMerchant } from "../../../helpers/ReduxHelpers";
import { FormSkeleton } from "../../../common/Skeletons";
import { RHFSearchableSelectField } from "../../../common/hook-form";
import MUIModal from "../../../common/MUIModal";
import _ from "lodash";
import { addressString, filterByProp } from "../../../helpers/UtilityHelper";
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { apiConstants, post } from "../../../common/constants";
import { useSnackbar } from "notistack";
import { NAVY, SUBTLE, PRIMARY_BLUE, BORDER, LIGHT_BG, INFO_BG, INFO } from "../../../styles/foundation/colors";
import { FONT, SIZE, WEIGHT } from "../../../styles/foundation/typography";
import RtoAddressSelector from "../RtoAddressSelector";

function MetaChip({ label, value }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 10, fontWeight: WEIGHT.medium, color: SUBTLE, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY }}>
                {value || '—'}
            </Typography>
        </Box>
    );
}

function PickupLocation({ setActiveStep }) {
    const merchant = getMerchant();
    const { enqueueSnackbar } = useSnackbar();
    const { trigger, watch, setValue } = useFormContext();

    const { data, isLoading, isFetching, refetch } = useGetMerchantQuery({ id: merchant.id });

    const pickupLocations = filterByProp(data?.pickup_details, "is_deleted") || [];
    const pickupLocationOptions = _(pickupLocations)
        .filter({ status: 'ACTIVE', address_type: 'PICKUP' })
        .map((loc) => ({ value: loc.id, label: addressString(loc) }))
        .value();

    const selectedId = watch('pickupLocation');
    const selectedLocation = pickupLocations.find((loc) => loc.id === selectedId) || null;

    const [modalOpen, setModalOpen] = useState(false);
    const [editPhone, setEditPhone] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (selectedLocation) {
            setValue('pickupContactPhone', selectedLocation.contactNumber || '');
            setValue('pickupContactEmail', selectedLocation.email || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    const openModal = () => {
        setEditPhone(selectedLocation?.contactNumber || '');
        setEditEmail(selectedLocation?.email || '');
        setModalOpen(true);
    };

    const handleSave = () => {
        setSaving(true);
        post(`${apiConstants.BASE_LOCAL}${apiConstants.ADD_LOCATION}/${merchant.id}`, {
            ...selectedLocation,
            contactNumber: editPhone,
            email: editEmail,
        })
            .then(() => {
                setValue('pickupContactPhone', editPhone);
                setValue('pickupContactEmail', editEmail);
                refetch();
                enqueueSnackbar('Pickup contact details updated successfully!', { variant: 'success' });
                setModalOpen(false);
            })
            .catch((err) => {
                enqueueSnackbar(err?.response?.data?.message || 'Failed to update contact details.', { variant: 'error' });
            })
            .finally(() => setSaving(false));
    };

    if (isLoading || isFetching) return <FormSkeleton />;

    const onNextHandler = async () => {
        const status = await trigger(['pickupLocation', 'rtoLocation']);
        if (status) setActiveStep(2);
    };

    const onKeyPressHandler = (e) => {
        if (e.charCode === 13) onNextHandler();
    };

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <RHFSearchableSelectField
                        options={pickupLocationOptions}
                        width="100%"
                        label="Please Select Pickup Location"
                        name="pickupLocation"
                        onKeyPress={onKeyPressHandler}
                        autoFocus={true}
                    />
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: SUBTLE, mt: 0.5 }}>
                        Pickup location not listed?{' '}
                        <a href="/merchant-step2" target="_blank" rel="noopener noreferrer" style={{ color: PRIMARY_BLUE, textDecoration: 'none', fontWeight: 500 }}>
                            Create new
                        </a>
                    </Typography>
                </Grid>

                {selectedLocation && (
                    <Grid item xs={12}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
                            onClick={openModal}
                            sx={{ fontFamily: FONT, fontSize: 12, fontWeight: WEIGHT.medium, textTransform: 'none', borderRadius: '8px' }}
                        >
                            Edit Pickup Details
                        </Button>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <RtoAddressSelector
                        locations={_.filter(pickupLocations, { status: 'ACTIVE', address_type: 'PICKUP' })}
                        pickupLocationId={selectedId}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Stack direction="row" columnGap={1}>
                        <Button variant="contained" size="small" color="error" onClick={() => setActiveStep(0)}>Back</Button>
                        <Button variant="contained" size="small" onClick={onNextHandler}>Next</Button>
                    </Stack>
                </Grid>
            </Grid>

            <MUIModal
                open={modalOpen}
                handleClose={() => setModalOpen(false)}
                title="Pickup Address Details"
                maxWidth="sm"
                action={
                    <LoadingButton
                        loading={saving}
                        variant="contained"
                        size="small"
                        onClick={handleSave}
                        sx={{ fontFamily: FONT, fontSize: 12, fontWeight: WEIGHT.medium, textTransform: 'none', borderRadius: '8px', px: 2.5 }}
                    >
                        Save & Update
                    </LoadingButton>
                }
            >
                <Stack spacing={0}>
                    {/* Location header */}
                    <Box sx={{ bgcolor: `${PRIMARY_BLUE}10`, border: `1px solid ${PRIMARY_BLUE}30`, borderRadius: '10px', p: 2, mb: 2.5 }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Box sx={{ bgcolor: PRIMARY_BLUE, borderRadius: '8px', p: 0.8, display: 'flex', mt: 0.2 }}>
                                <LocationOnOutlinedIcon sx={{ fontSize: 18, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY, lineHeight: 1.3 }}>
                                    {selectedLocation?.name}
                                </Typography>
                                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: SUBTLE, mt: 0.4, lineHeight: 1.5 }}>
                                    {selectedLocation?.address_line}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Address meta grid */}
                    <Box sx={{ bgcolor: LIGHT_BG, border: `1px solid ${BORDER}`, borderRadius: '10px', p: 2, mb: 2.5 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}><MetaChip label="City" value={selectedLocation?.city} /></Grid>
                            <Grid item xs={4}><MetaChip label="State" value={selectedLocation?.state} /></Grid>
                            <Grid item xs={4}><MetaChip label="Pincode" value={selectedLocation?.pincode} /></Grid>
                            <Grid item xs={6}><MetaChip label="Country" value={selectedLocation?.country} /></Grid>
                            <Grid item xs={6}><MetaChip label="GSTIN" value={selectedLocation?.gstin} /></Grid>
                        </Grid>
                    </Box>

                    {/* Edit section divider */}
                    <Divider sx={{ mb: 2.5 }}>
                        <Chip
                            label="Edit Contact Details"
                            size="small"
                            sx={{ fontFamily: FONT, fontSize: 11, fontWeight: WEIGHT.medium, color: SUBTLE, bgcolor: '#fff', border: `1px solid ${BORDER}` }}
                        />
                    </Divider>

                    {/* Editable fields */}
                    <Stack spacing={2} sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Mobile Number"
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value)}
                            inputProps={{ maxLength: 10 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PhoneOutlinedIcon sx={{ fontSize: 16, color: SUBTLE }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ '& .MuiInputBase-root': { fontFamily: FONT, fontSize: SIZE.xs }, '& .MuiInputLabel-root': { fontFamily: FONT, fontSize: SIZE.xs } }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Email Address"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailOutlinedIcon sx={{ fontSize: 16, color: SUBTLE }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ '& .MuiInputBase-root': { fontFamily: FONT, fontSize: SIZE.xs }, '& .MuiInputLabel-root': { fontFamily: FONT, fontSize: SIZE.xs } }}
                        />
                    </Stack>

                    {/* Info note */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8, bgcolor: INFO_BG, border: `1px solid ${INFO}30`, borderRadius: '8px', px: 1.5, py: 1 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: INFO, mt: 0.2, flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: FONT, fontSize: 11, color: INFO, lineHeight: 1.5 }}>
                            Changes to mobile number and email will be permanently saved to this pickup address and reflected across future shipments.
                        </Typography>
                    </Box>
                </Stack>
            </MUIModal>
        </>
    );
}

export default PickupLocation;
